import { HttpClient } from '@angular/common/http';
import { EventEmitter } from '@angular/core';
import { PropertyMediaInfo } from '@miq/utils';
import { Observable } from 'rxjs';
import { CustomDataRetrievers } from '../custom-data/custom-data.const';
import { ReportExportViewService } from '../report-export-view.service';
import { Layout } from './blueprint.model';
import { DataObject, EditingBoxConfigItem, ReportOptions, ReportRecord } from './report.model';

export function isNumericDataType(val: string): val is NumericDataType {
  // constant initialization required to prevent angular building issues
  const isDataType = ['numeric', 'id', 'integer'].includes(val);
  return isDataType;
}

export function isStringDataType(val: string): val is StringDataType {
  // constant initialization required to prevent angular building issues
  const isDataType = ['text', 'textarea', 'string'].includes(val);
  return isDataType;
}
export function isDateDataType(val: string): val is DateDataType {
  // constant initialization required to prevent angular building issues
  const isDataType = ['month', 'date', 'timestamp with time zone'].includes(val);
  return isDataType;
}
export function isBooleanDataType(val: string): val is BooleanDataType {
  // constant initialization required to prevent angular building issues
  const isDataType = ['boolean'].includes(val);
  return isDataType;
}

export type NumericDataType = 'numeric' | 'id' | 'integer';
export type StringDataType = 'text' | 'textarea' | 'string';
export type DateDataType = 'month' | 'date' | 'timestamp with time zone';
export type BooleanDataType = 'boolean';

export type FieldDataType = NumericDataType | StringDataType | DateDataType | BooleanDataType;

export type ViewTemplateCatalog = ViewTemplate[];

export interface ViewTemplate {
  templateId: number;
  imageUrl: string;
  templateName: string;
  viewId?: number;
}

export interface TemplateFieldsCatalog {
  [fieldId: number]: TemplateField;
  coverSlide?: TemplateField;
}

/**
 * This type represents an object that contains the {@link MappedTemplateField}
 * attributes (id, label, value, & options). However, the value attribute is a
 * string or string[] instead of an observable.
 *
 * @example
 * {
 *   "id": 16,
 *   "label": "Property Description",
 *   "value": "West End Offices"
 * }
 */
export interface ResolvedTemplateField {
  id: number;
  label: string;
  value: string | string[] | any;
}

export interface TemplateField {
  id: number;
  label: string;
  displayFnArguments: DisplayFnArgumentsInfo[];
  /**
   * Contains the expected order to be used and displayed by the display
   * function.
   * For instance, it might be used by the address field where we receive all
   * the columns that can form an address but the expected order depends on the
   * country or requirement.
   *
   * @example
   * {
   *   // the next config would return: "Address, City, Postal Code"
   *   displayFnArgumentsOrder: [851, 854, 856]
   * }
   */
  displayFnArgumentsOrder?: number[];
  edpUpdateConf?: EditingBoxConfigItem | EditingBoxConfigItem[];
  value?: string | string[];
  options?: { [opt: string]: any };
  condition?: string;
  fieldDataToValidate?: string;
  /** Attribute name of report attribute, used to retrieve values like report name */
  reportAttribute: 'reportId' | 'name' | 'description' | 'entity' | 'reportType' | 'createdBy' | 'sharedWith';
  fieldLevelJoinString?: boolean;
  isPPTHasSpecificFormat?: boolean;
  /**
   * String configured in the blueprint json, mainly used for windward reports
   *
   * @see CustomDataRetrievers
   */
  customDataService?: CustomDataRetrievers;
  /**
   * custom data payload can be anything that is configured in the blueprint.
   * this is usually an array of template field IDs
   */
  customDataPayload?: any;
  isPublishedPrimaryImagesOnly?: boolean;
  pickFromEntity?: boolean;
}

export type MappedTemplateField = Pick<TemplateField, 'id' | 'label'> & {
  value: Observable<string | string[]>;
  options?: { [opts: string]: any };
};

export type RequiredFieldsFromColumn = Required<
  Pick<DisplayFnArgumentsInfo, 'id' | 'field' | 'dataType' | 'dataFormat' | 'isUpdatable' | 'condition'>
>;

export interface LovValues {
  key: string;
  value: string;
}

export interface DisplayFnArgumentsInfo {
  id: number;
  columnId?: number;
  uomId?: number;
  entity?: string;
  isUpdatable?: boolean;
  field?: string;
  path?: string;
  dataType?: FieldDataType;
  dataFormat?: string;
  uomArgument?: DisplayFnArgumentsInfo;
  isPrefix?: boolean;
  condition?: string;
  dataFormattingDesc?: string;
  externalRatingDesc?: string;
  lovValues?: Observable<LovValues[]>;
  isRecordTableData?: boolean;
  /**
   * Optional attribute that can be added dynamically. This is particularly used
   * when the expected value from the argument is a number, and it should be
   * formatted based on locale. i.e. Some european countries uses a dot to
   * separate thousands, instead of the comma, as in the USA
   */
  location?: string;
  lookupCategory?: string;
}

export interface DisplayFunctionOptions {
  locale: number;
  jsonData: any;
  httpClient: HttpClient;
  reportExportViewService: ReportExportViewService;
  symbols?: DataObject;
  isPPTHasSpecificFormat?: boolean;
  reportId?: number;
  reportOptions?: ReportOptions;
  /** @see TemplateField.displayFnArgumentsOrder */
  displayFnArgumentsOrder: number[];
  templateId: number;
  urlPrefix: string;
  urlSplitter: string;
}

export type DisplayFunctionPPT = (
  record: ReportRecord | ReportRecord[],
  options: DisplayFunctionOptions,
  ...displayFnArguments: Array<DisplayFnArgumentsInfo>
) => string | string[] | Observable<string | string[]> | PropertyMediaInfo[];

export type VisisbleFunctionPPT = (
  record: ReportRecord | ReportRecord[],
  settings,
  ...displayFnArguments: Array<DisplayFnArgumentsInfo>
) => boolean;

export type LabelDisplayFunctionPPT = (
  record: ReportRecord | ReportRecord[],
  ...displayFnArguments: Array<DisplayFnArgumentsInfo>
) => string;

export type NavigationRouteFunctionPPTSettings = {
  templateId: number;
  field: TemplateField;
  subAttribute?: {
    urlSection: string;
    dataPath: string;
  };
};

export type NavigationRouteFunctionPPT = (
  record: ReportRecord,
  settings?: NavigationRouteFunctionPPTSettings
) => string[];

export interface PPTPropertyInterface {
  displayFn: DisplayFunctionPPT;
  routeFn?: NavigationRouteFunctionPPT;
  isVisible?: VisisbleFunctionPPT;
  labelDisplayFn?: LabelDisplayFunctionPPT;
  displayFnArguments?: DisplayFnArgumentsInfo[];
  isTableDataUpdatable?: boolean;
  lookupCategory?: string;
}

export interface TemplateFieldsDisplayFn {
  [fieldId: number]: PPTPropertyInterface;
}

export interface ReportAllRecordsImage {
  [id: string]: ReportRecordImage[];
}

export interface ReportRecordImage {
  reportImageId: number;
  imageId: number;
}

export interface LayoutAndRecords {
  layout: Layout;
  splitArray: ReportRecord[];
}

export interface RawColumn {
  columnId: number;
  dataType: string;
  field: string;
  header: string;
  inputType: string;
  lookupCategory: string;
  options: { [key: string]: any };
  parent: RawColumnParent;
  type: string;
}

export interface RawColumnParent {
  idField?: string;
  update?: { [key: string]: any };
  entity: string;
}

export interface SortEvent {
  sortBy: string;
  sortOrder: number;
  onSort?: EventEmitter<any>;
}

export interface PropertyGroup {
  associationID: number;
  groupID: number;
  groupName: string;
  recordID: number;
  relatedRecordID: number;
  viewID: number;
  isDisabled?: boolean;
}

export interface PropertyGroupQuery {
  recordID: number;
  relatedRecordID: number;
  groupID?: number;
  groupName?: string;
}

export interface VisibilitySettings {
  templateId: number;
  field: TemplateField;
  reportOptions?: ReportOptions;
}
